import { text3, text5, textPrimary } from '@osmosis/tailwind-ui/styles/text';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { P, H3 } from '@osmosis/tailwind-ui/components/Text';

import React, { useState } from 'react';
import { useParams } from 'next/navigation';
import { usePagesContext } from '../../../context/PagesProvider';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';

import {
  PlaylistGroupProps,
  PlaylistType,
} from '@osmosis/shared/types/playlist';
import { useIntl } from 'react-intl';
import { getIntlMap } from '../../../lib/shared/playlist/utils';

type Props = {
  playlistGroup: PlaylistGroupProps;
};

const playlistDataTestIds = {
  [PlaylistType.WATCH_LATER]: 'watch-later',
  [PlaylistType.USER_OWNED]: 'user-owned',
  [PlaylistType.FOLLOWING]: 'following',
  [PlaylistType.RECENTLY_WATCHED]: 'recently-watched',
};

/**
 * PlaylistGroupAccordion.
 */
const PlaylistGroupAccordion = (props: Props): JSX.Element => {
  const { playlistGroup } = props;
  const [isOpen, setIsOpen] = useState(true);
  const { push } = usePagesContext();
  const params = useParams();

  const playlistUrl = params?.id;
  const intl = useIntl();

  const { playlists } = playlistGroup;

  const handleToggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const handleNavigate = (url: string) => {
    push(`/playlist/${url}`);
  };

  const map = getIntlMap(intl);

  const collapsePlaylists = intl.formatMessage({
    id: 'playlist.group.accordian.collapse',
    defaultMessage: 'Collapse playlists',
  });

  const expandPlaylists = intl.formatMessage({
    id: 'playlist.group.accordian.expand',
    defaultMessage: 'Expand playlists',
  });

  return (
    <Box>
      <Flex
        data-testid={`playlist.group-accordion-${
          playlistDataTestIds[playlistGroup.type]
        }`}
        className={`mb-4 justify-between`}
      >
        <H3>{map[playlistGroup.type]}</H3>
        {!!playlists.length && (
          <IconButton
            data-testid="toggle-accordian-button"
            onClick={handleToggleOpen}
            variant="transparent"
            icon={<Icon icon={isOpen ? faChevronUp : faChevronDown} />}
            ariaLabel={isOpen ? collapsePlaylists : expandPlaylists}
            className={`text-base ${text3}`}
          />
        )}
      </Flex>
      {isOpen && (
        <Box data-testid="playlist-container-element" className={`ml-4`}>
          {playlists.map(
            (p) =>
              p.url && (
                <Box key={p.playlistID} className={`mb-2`}>
                  <Button
                    data-testid={`playlist-${p.title}-button`}
                    onClick={() => handleNavigate(p.url)}
                    variant="transparent"
                    className={`${text5} w-full justify-start text-left`}
                  >
                    <P
                      className={`${
                        playlistUrl === p.url ? textPrimary : 'text-gray-700'
                      } font-normal`}
                    >
                      {p.title}
                    </P>
                  </Button>
                </Box>
              ),
          )}
        </Box>
      )}
    </Box>
  );
};

export default PlaylistGroupAccordion;
